import React, { useEffect, useState } from "react";
import { TextField, CircularProgress, Grid, Typography, InputAdornment, Tabs, Tab, MenuItem, FormControlLabel, Switch } from "@mui/material";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { useTheme } from "../../contexts/ThemeContext.tsx";
import { useFeedback } from "../../contexts/FeedbackContext.tsx";
import { usePlanoContext } from "../../contexts/PlanoContext.tsx";
import { parseJwt } from "../../utils/token.util.ts";
import { formatBrazilPhone } from "../../utils/phone.util.ts";
import { maskCep } from "../../utils/address.util.ts";
import usuarioService from "../../services/usuarioService.ts";
import empresaService from "../../services/empresaService.ts";
import setupService from "../../services/setupService.ts";
import { invalidarPlanLimitsCache } from "../../hooks/usePlanLimits.ts";
import ErrorBanner from "../../ui/ErrorBanner.tsx";
import Badge from "../../ui/Badge.tsx";
import Button from "../../ui/Button.tsx";
import Modal from "../../ui/Modal.tsx";
import "../../styles/design-system.css";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import PermissoesPreview from "../../components/PermissoesPreview/index.tsx";
import GestaoUsuarios from "../../components/GestaoUsuarios/index.tsx";
import { usePermissoes } from "../../contexts/PermissoesContext.tsx";
import { useSearchParams } from "react-router-dom";

// Mesmo critério usado no Sidebar pra distinguir conta interna ControlSIS de
// conta cliente — não existe "role"/cargo no domínio de Usuario hoje.
const DOMINIO_ADMIN = "@controlsis.com.br";

// Mesma receita de cores dos Drawers — sem ThemeProvider do MUI, estados de
// label/borda caem no tema claro padrão do MUI, sem contraste no dark mode.
const fieldSx = {
  "& .MuiInputLabel-root": { color: "var(--ink-faint)" },
  "& .MuiInputLabel-root.Mui-focused": { color: "var(--blue)" },
  "& .MuiInputLabel-root.Mui-disabled": { color: "var(--ink-faint)" },
  "& .MuiOutlinedInput-root": {
    color: "var(--ink)",
    "& fieldset": { borderColor: "var(--border-strong)" },
    "&:hover fieldset": { borderColor: "var(--border-strong)" },
    "&.Mui-focused fieldset": { borderColor: "var(--blue)" },
    "&.Mui-disabled fieldset": { borderColor: "var(--border)" },
  },
  "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: "var(--ink-faint)", color: "var(--ink-faint)" },
  "& .MuiFormHelperText-root": { color: "var(--ink-faint)" },
};

// Mesma armadilha do fieldSx, em outro componente: sem ThemeProvider, o MUI
// pinta o label de um Switch desabilitado com a cor "disabled" do tema CLARO
// (cinza escuro), que no fundo escuro fica ilegível. O sx do root não vence —
// a regra do .MuiFormControlLabel-label é mais específica.
const switchLabelSx = {
  display: "flex",
  ml: 0,
  color: "var(--ink)",
  "& .MuiFormControlLabel-label": { color: "var(--ink)" },
  "& .MuiFormControlLabel-label.Mui-disabled": { color: "var(--ink-faint)" },
};

// Idem — Tabs do MUI sem ThemeProvider caem no azul/cinza padrão do tema
// claro, sem contraste nenhum no dark mode.
const tabsSx = {
  minHeight: 42,
  borderBottom: "1px solid var(--border)",
  "& .MuiTabs-indicator": { backgroundColor: "var(--blue)", height: 2.5, borderRadius: 2 },
  "& .MuiTab-root": {
    textTransform: "none",
    fontWeight: 700,
    fontSize: 13.8,
    minHeight: 42,
    padding: "10px 4px",
    marginRight: 28,
    color: "var(--ink-faint)",
    "&:hover": { color: "var(--ink-soft)" },
  },
  "& .MuiTab-root.Mui-selected": { color: "var(--blue)" },
};

// "Leandro Costa" -> "LC" (mesma regra usada nos perfis do resto do sistema);
// cai pras duas primeiras letras do e-mail quando ainda não tem nome salvo.
const getIniciais = (nome: string, email?: string) => {
  const partes = (nome || "").trim().split(/\s+/).filter(Boolean);
  if (partes.length > 0) {
    const primeira = partes[0][0] || "";
    const ultima = partes.length > 1 ? partes[partes.length - 1][0] : "";
    return (primeira + ultima).toUpperCase();
  }
  return (email ?? "?").slice(0, 2).toUpperCase();
};

interface EmpresaForm {
  nome: string;
  fantasia: string;
  email: string;
  cnpj: string;
  cep: string;
  logradouro: string;
  numero: string;
  municipio: string;
  bairro: string;
  uf: string;
}

const EMPRESA_VAZIA: EmpresaForm = {
  nome: "", fantasia: "", email: "", cnpj: "",
  cep: "", logradouro: "", numero: "", municipio: "", bairro: "", uf: "",
};

const ConfiguracoesPage = () => {
  const { darkMode, setTheme } = useTheme();
  const { showFeedback } = useFeedback();
  const { planoNome } = usePlanoContext();

  const claims = parseJwt(localStorage.getItem("token"));
  const emailUsuario = claims?.sub as string | undefined;
  const usuarioId = claims?.usuarioId as string | undefined;
  const isAdmin = !!emailUsuario && emailUsuario.toLowerCase().endsWith(DOMINIO_ADMIN);
  const tipoConta = isAdmin ? "Equipe ControlSIS" : planoNome || "Sem plano";

  const { usuario: usuarioLogado, isAdm, isLoading: carregandoPermissoes } = usePermissoes();

  // ?aba= existe para o redirecionamento de /usuario/listagem, que era uma tela
  // propria antes da gestao de usuarios virar aba daqui. Links e favoritos
  // antigos continuam chegando no lugar certo.
  const [searchParams] = useSearchParams();
  const abaDaUrl = searchParams.get("aba");
  const [aba, setAba] = useState<"perfil" | "empresa" | "usuarios" | "seguranca">(
    abaDaUrl === "empresa" || abaDaUrl === "usuarios" || abaDaUrl === "seguranca" ? abaDaUrl : "perfil"
  );

  // Quem nao e ADM nao pode ficar parado numa aba que nao existe para ele - o
  // MUI renderizaria as Tabs sem nenhuma selecionada e o corpo vazio.
  //
  // O `!carregandoPermissoes` nao e detalhe: enquanto o contexto carrega,
  // isAdm ainda e false, e sem essa guarda o efeito derrubava o ?aba=usuarios
  // de volta para "perfil" antes mesmo de saber quem era o usuario.
  useEffect(() => {
    if (carregandoPermissoes || isAdm) return;
    if (aba === "usuarios" || aba === "empresa") setAba("perfil");
  }, [aba, isAdm, carregandoPermissoes]);

  // ---- Dados pessoais (usuário logado) ----
  const [original, setOriginal] = useState({ nome: "", telefone: "" });
  const [nome, setNome] = useState("");
  const [telefone, setTelefone] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // Antes daqui isto buscava GET /usuario/listar e filtrava pelo proprio e-mail,
  // porque nao existia um endpoint de "quem sou eu". Agora existe, e o
  // PermissoesContext ja o carregou (com cache): uma requisicao a menos nesta
  // tela, num backend cujo pool e de 3 conexoes. Some tambem o risco de a lista
  // e o token discordarem sobre quem esta logado.
  useEffect(() => {
    if (!usuarioLogado) return;
    const dados = {
      nome: usuarioLogado.nome || "",
      telefone: formatBrazilPhone(usuarioLogado.telefone || "") || usuarioLogado.telefone || "",
    };
    setOriginal(dados);
    setNome(dados.nome);
    setTelefone(dados.telefone);
  }, [usuarioLogado]);

  const handleCancelar = () => {
    setNome(original.nome);
    setTelefone(original.telefone);
  };

  const handleSalvar = async () => {
    if (!usuarioId) return;
    try {
      setIsSaving(true);
      const resp = await usuarioService.update({ id: usuarioId, telefone, nome });
      const dados = { nome: resp?.data?.nome ?? nome, telefone: formatBrazilPhone(resp?.data?.telefone) || resp?.data?.telefone || telefone };
      setOriginal(dados);
      setNome(dados.nome);
      setTelefone(dados.telefone);
      showFeedback("Dados atualizados com sucesso!", "success");
    } catch (err: any) {
      showFeedback(err.message || "Erro ao salvar alterações", "error");
    } finally {
      setIsSaving(false);
    }
  };

  // ---- Dados da empresa ----
  const [empresaOriginal, setEmpresaOriginal] = useState<EmpresaForm>(EMPRESA_VAZIA);
  const [empresa, setEmpresa] = useState<EmpresaForm>(EMPRESA_VAZIA);
  const [isLoadingEmpresa, setIsLoadingEmpresa] = useState(true);
  const [isSavingEmpresa, setIsSavingEmpresa] = useState(false);
  const [buscandoCep, setBuscandoCep] = useState(false);

  // ---- Configurações da empresa (hoje só instrumentadores por cirurgia) ----
  // Só o usuário com perfil "Admin" — quem cadastrou a empresa — pode alterar.
  // Quem decide é o backend (EmpresaService.podeAlterarConfiguracoes); aqui a
  // flag só serve pra renderizar em somente-leitura em vez de deixar tentar e
  // tomar erro.
  const [maxInstrumentadores, setMaxInstrumentadores] = useState(3);
  const [maxInstrumentadoresOriginal, setMaxInstrumentadoresOriginal] = useState(3);
  const [podeAlterarConfiguracoes, setPodeAlterarConfiguracoes] = useState(false);
  const [logAtividadeAtivo, setLogAtividadeAtivo] = useState(false);
  const [logAtividadeOriginal, setLogAtividadeOriginal] = useState(false);
  const [isSavingConfig, setIsSavingConfig] = useState(false);

  // ---- Troca de senha do proprio usuario ----
  const [senhaAtual, setSenhaAtual] = useState("");
  const [novaSenha, setNovaSenha] = useState("");
  const [confirmarSenha, setConfirmarSenha] = useState("");
  const [erroSenha, setErroSenha] = useState<string | null>(null);
  const [isSavingSenha, setIsSavingSenha] = useState(false);

  useEffect(() => {
    const carregarEmpresa = async () => {
      try {
        const dto: any = await empresaService.getAtual();
        const dados: EmpresaForm = {
          nome: dto?.nome || "",
          fantasia: dto?.fantasia || "",
          email: dto?.email || "",
          cnpj: dto?.cnpj || "",
          cep: dto?.cep ? maskCep(dto.cep) : "",
          logradouro: dto?.logradouro || "",
          numero: dto?.numero || "",
          municipio: dto?.municipio || "",
          bairro: dto?.bairro || "",
          uf: dto?.uf || "",
        };
        setEmpresaOriginal(dados);
        setEmpresa(dados);
        setApiKeyGeradaEm(dto?.integracaoApiKeyGeradaEm || null);
        const max = dto?.maxInstrumentadores || 3;
        setMaxInstrumentadores(max);
        setMaxInstrumentadoresOriginal(max);
        setPodeAlterarConfiguracoes(Boolean(dto?.podeAlterarConfiguracoes));
        const logAtivo = Boolean(dto?.logAtividadeAtivo);
        setLogAtividadeAtivo(logAtivo);
        setLogAtividadeOriginal(logAtivo);
      } catch {
        // Se a busca falhar, o card fica vazio pro usuário preencher — resto da tela segue normal.
      } finally {
        setIsLoadingEmpresa(false);
      }
    };
    carregarEmpresa();
  }, []);

  // ---- Integração Delphi (API key) ----
  const [features, setFeatures] = useState<any>(null);
  const [apiKeyGeradaEm, setApiKeyGeradaEm] = useState<string | null>(null);
  const [isGerandoApiKey, setIsGerandoApiKey] = useState(false);
  const [confirmRegenerarAberto, setConfirmRegenerarAberto] = useState(false);
  const [apiKeyGerada, setApiKeyGerada] = useState<string | null>(null);

  useEffect(() => {
    const carregarFeatures = async () => {
      try {
        const result = await setupService.listFeatures();
        setFeatures(result?.data?.funcionalidade || {});
      } catch {
        setFeatures({});
      }
    };
    carregarFeatures();
  }, []);

  const temIntegracaoApi = !!features?.integracaoApi;

  const executarGeracaoApiKey = async () => {
    setConfirmRegenerarAberto(false);
    setIsGerandoApiKey(true);
    try {
      const resp = await setupService.generateApiKey();
      setApiKeyGerada(resp?.data?.apiKey || "");
      setApiKeyGeradaEm(new Date().toISOString());
    } catch (err: any) {
      showFeedback(err.message || "Erro ao gerar a API key de integração", "error");
    } finally {
      setIsGerandoApiKey(false);
    }
  };

  const handleGerarApiKey = () => {
    // Já existe uma chave ativa -> confirma antes, porque gerar uma nova
    // invalida a anterior imediatamente (é o mecanismo de revogação).
    if (apiKeyGeradaEm) {
      setConfirmRegenerarAberto(true);
    } else {
      executarGeracaoApiKey();
    }
  };

  const handleCopiarApiKey = () => {
    if (!apiKeyGerada) return;
    navigator.clipboard.writeText(apiKeyGerada);
    showFeedback("API key copiada para a área de transferência", "success");
  };

  const handleCancelarEmpresa = () => setEmpresa(empresaOriginal);

  const handleCepChangeEmpresa = async (value: string) => {
    const masked = maskCep(value);
    setEmpresa((f) => ({ ...f, cep: masked }));
    const raw = masked.replace(/\D/g, "");
    if (raw.length !== 8) return;
    setBuscandoCep(true);
    try {
      const resp = await fetch(`https://viacep.com.br/ws/${raw}/json/`);
      const data = await resp.json();
      if (data.erro) {
        showFeedback("CEP não encontrado", "error");
      } else {
        setEmpresa((f) => ({
          ...f,
          logradouro: data.logradouro || f.logradouro,
          bairro: data.bairro || f.bairro,
          municipio: data.localidade || f.municipio,
          uf: data.uf || f.uf,
        }));
      }
    } catch {
      showFeedback("Erro ao buscar o CEP", "error");
    } finally {
      setBuscandoCep(false);
    }
  };

  const handleSalvarEmpresa = async () => {
    try {
      setIsSavingEmpresa(true);
      const resp = await empresaService.alterar({
        nome: empresa.nome,
        email: empresa.email,
        fantasia: empresa.fantasia,
        logradouro: empresa.logradouro,
        numero: empresa.numero,
        municipio: empresa.municipio,
        bairro: empresa.bairro,
        uf: empresa.uf,
        cep: empresa.cep,
      });
      const d = resp?.data;
      const dados: EmpresaForm = {
        ...empresa,
        nome: d?.nome ?? empresa.nome,
        fantasia: d?.fantasia ?? empresa.fantasia,
        email: d?.email ?? empresa.email,
        cep: d?.cep ? maskCep(d.cep) : empresa.cep,
        logradouro: d?.logradouro ?? empresa.logradouro,
        numero: d?.numero ?? empresa.numero,
        municipio: d?.municipio ?? empresa.municipio,
        bairro: d?.bairro ?? empresa.bairro,
        uf: d?.uf ?? empresa.uf,
      };
      setEmpresaOriginal(dados);
      setEmpresa(dados);
      showFeedback(resp?.message || "Dados da empresa atualizados com sucesso!", "success");
    } catch (err: any) {
      showFeedback(err.message || "Erro ao salvar dados da empresa", "error");
    } finally {
      setIsSavingEmpresa(false);
    }
  };

  const handleAlterarSenha = async () => {
    setErroSenha(null);

    if (novaSenha.length < 6) {
      setErroSenha("A nova senha deve ter pelo menos 6 caracteres.");
      return;
    }
    if (novaSenha !== confirmarSenha) {
      setErroSenha("A confirmação não confere com a nova senha.");
      return;
    }

    try {
      setIsSavingSenha(true);
      const resp = await usuarioService.alterarSenha(senhaAtual, novaSenha);
      setSenhaAtual("");
      setNovaSenha("");
      setConfirmarSenha("");
      showFeedback(resp?.message || "Senha alterada com sucesso!", "success");
    } catch (err: any) {
      setErroSenha(err.message || "Não foi possível alterar a senha.");
    } finally {
      setIsSavingSenha(false);
    }
  };

  const handleSalvarConfiguracoes = async () => {
    try {
      setIsSavingConfig(true);
      const resp = await empresaService.alterar({ maxInstrumentadores, logAtividadeAtivo });
      const salvo = resp?.data?.maxInstrumentadores ?? maxInstrumentadores;
      setMaxInstrumentadores(salvo);
      setMaxInstrumentadoresOriginal(salvo);
      setLogAtividadeOriginal(logAtividadeAtivo);
      // O formulário de nova cirurgia lê esse limite de /plano/limites, que é
      // cacheado por 5 min — sem invalidar, a mudança só apareceria depois.
      invalidarPlanLimitsCache();
      showFeedback("Configurações da empresa atualizadas!", "success");
    } catch (err: any) {
      showFeedback(err.message || "Erro ao salvar configurações", "error");
    } finally {
      setIsSavingConfig(false);
    }
  };

  return (
    <div className="ds ds-settings-page">
      <div className="ds-page-head">
        <div>
          <h1>Configurações</h1>
          <p>Preferências da sua conta e da empresa.</p>
        </div>
      </div>

      <Tabs
        value={aba}
        onChange={(_, valor) => setAba(valor)}
        sx={tabsSx}
      >
        <Tab label="Perfil" value="perfil" disableRipple />
        {/*
          Empresa e Usuarios sao de ADM. Deixar a aba Empresa visivel para todos
          contradizia o proprio painel de permissoes logo abaixo, que diz ao
          operador "X Dados e configuracoes da empresa" - e o PATCH devolveria
          403 depois de ele preencher o formulario.
        */}
        {/*
          Enquanto as permissoes carregam nao da para saber se estas abas
          existem. Omiti-las e depois faze-las aparecer muda a largura do strip
          e as abas "pulam" debaixo do cursor; por isso ficam como placeholder
          desabilitado ate a resposta chegar.
        */}
        {carregandoPermissoes && <Tab label="Empresa" value="empresa" disabled disableRipple />}
        {carregandoPermissoes && <Tab label="Usuários" value="usuarios" disabled disableRipple />}
        {!carregandoPermissoes && isAdm && <Tab label="Empresa" value="empresa" disableRipple />}
        {!carregandoPermissoes && isAdm && <Tab label="Usuários" value="usuarios" disableRipple />}
        <Tab label="Segurança e Preferências" value="seguranca" disableRipple />
      </Tabs>

      <div style={{ marginTop: 24 }}>
        {aba === "perfil" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div className="ds-card ds-profile-head">
              <div className="ds-profile-avatar">{getIniciais(nome, emailUsuario)}</div>
              <div className="ds-profile-info" style={{ flex: 1, minWidth: 0 }}>
                <h2 style={{ wordBreak: "break-word" }}>{nome || emailUsuario || "Usuário"}</h2>
                <div className="ds-profile-meta">
                  <span><MailOutlineIcon fontSize="inherit" /> {emailUsuario || "—"}</span>
                </div>
                <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <Badge color={isAdmin ? "blue" : "teal"}>{tipoConta}</Badge>
                  {usuarioLogado && <Badge color="blue">{usuarioLogado.tipoRotulo}</Badge>}
                </div>
              </div>
            </div>

            {/*
              Mesma lista do formulário de usuários, em modo consulta. Existe
              para quem perdeu um menu na migração descobrir POR QUÊ, em vez de
              achar que o sistema quebrou e abrir chamado.
            */}
            {usuarioLogado && (
              <PermissoesPreview
                tipo={usuarioLogado.tipo}
                podeExcluirAgendamento={usuarioLogado.podeExcluirAgendamento}
                podeVerLog={usuarioLogado.podeVerLog}
                somenteLeitura
              />
            )}

            <div className="ds-card" style={{ padding: 26 }}>
              <div className="ds-section-title">Dados Pessoais</div>
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Nome completo" size="small" fullWidth sx={fieldSx}
                    value={nome} onChange={(e) => setNome(e.target.value)}
                    placeholder="Seu nome completo"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Telefone" size="small" fullWidth sx={fieldSx}
                    value={telefone} onChange={(e) => setTelefone(formatBrazilPhone(e.target.value))}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField label="E-mail" size="small" fullWidth disabled sx={fieldSx} value={emailUsuario || ""} />
                </Grid>
              </Grid>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 24, paddingTop: 20, borderTop: "1px solid var(--border)" }}>
                <Button variant="ghost" size="sm" onClick={handleCancelar} disabled={isSaving}>
                  Cancelar
                </Button>
                <Button variant="primary" size="sm" onClick={handleSalvar} disabled={isSaving}>
                  {isSaving ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : "Salvar alterações"}
                </Button>
              </div>
            </div>
          </div>
        )}

        {aba === "empresa" && isAdm && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div className="ds-card" style={{ padding: 26 }}>
            <div className="ds-section-title">Dados da Empresa</div>

            {isLoadingEmpresa ? (
              <p style={{ color: "var(--ink-faint)", fontSize: 13.2 }}>Carregando...</p>
            ) : (
              <>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Razão social" size="small" fullWidth sx={fieldSx}
                      value={empresa.nome} onChange={(e) => setEmpresa((f) => ({ ...f, nome: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Nome fantasia" size="small" fullWidth sx={fieldSx}
                      value={empresa.fantasia} onChange={(e) => setEmpresa((f) => ({ ...f, fantasia: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="E-mail" size="small" fullWidth sx={fieldSx}
                      value={empresa.email} onChange={(e) => setEmpresa((f) => ({ ...f, email: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="CNPJ" size="small" fullWidth disabled sx={fieldSx}
                      value={empresa.cnpj}
                      helperText="Documento fiscal — não pode ser alterado por aqui"
                    />
                  </Grid>

                  <Grid item xs={12} sx={{ mt: 0.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "var(--ink)" }}>
                      Endereço
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label="CEP" size="small" fullWidth sx={fieldSx}
                      value={empresa.cep} onChange={(e) => handleCepChangeEmpresa(e.target.value)}
                      disabled={buscandoCep}
                      InputProps={{
                        endAdornment: buscandoCep ? <InputAdornment position="end"><CircularProgress size={16} /></InputAdornment> : undefined,
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Bairro" size="small" fullWidth sx={fieldSx}
                      value={empresa.bairro} onChange={(e) => setEmpresa((f) => ({ ...f, bairro: e.target.value }))}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label="Cidade" size="small" fullWidth sx={fieldSx}
                      value={empresa.municipio} onChange={(e) => setEmpresa((f) => ({ ...f, municipio: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="UF" size="small" fullWidth sx={fieldSx}
                      value={empresa.uf} onChange={(e) => setEmpresa((f) => ({ ...f, uf: e.target.value.toUpperCase() }))}
                      inputProps={{ maxLength: 2, style: { textTransform: "uppercase" } }}
                    />
                  </Grid>

                  <Grid item xs={8}>
                    <TextField
                      label="Rua" size="small" fullWidth sx={fieldSx}
                      value={empresa.logradouro} onChange={(e) => setEmpresa((f) => ({ ...f, logradouro: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="Número" size="small" fullWidth sx={fieldSx}
                      value={empresa.numero} onChange={(e) => setEmpresa((f) => ({ ...f, numero: e.target.value }))}
                    />
                  </Grid>
                </Grid>

                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 24, paddingTop: 20, borderTop: "1px solid var(--border)" }}>
                  <Button variant="ghost" size="sm" onClick={handleCancelarEmpresa} disabled={isSavingEmpresa}>
                    Cancelar
                  </Button>
                  <Button variant="primary" size="sm" onClick={handleSalvarEmpresa} disabled={isSavingEmpresa}>
                    {isSavingEmpresa ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : "Salvar alterações"}
                  </Button>
                </div>
              </>
            )}
          </div>

          <div className="ds-card" style={{ padding: 26 }}>
            <div className="ds-section-title">Instrumentadores</div>

            <Grid container spacing={2.5} alignItems="flex-start">
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  label="Instrumentadores por cirurgia"
                  value={maxInstrumentadores}
                  onChange={(e) => setMaxInstrumentadores(Number(e.target.value))}
                  disabled={!podeAlterarConfiguracoes || isLoadingEmpresa}
                  size="small"
                  fullWidth
                  sx={fieldSx}
                  InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value={1}>1 instrumentador</MenuItem>
                  <MenuItem value={2}>2 instrumentadores</MenuItem>
                  <MenuItem value={3}>3 instrumentadores</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={8}>
                <p style={{ fontSize: 12.6, color: "var(--ink-faint)", margin: "6px 0 0", maxWidth: 520 }}>
                  Define quantos campos de instrumentador aparecem ao agendar uma cirurgia.
                  Cirurgias já cadastradas com mais instrumentadores do que isso continuam
                  intactas e podem ser editadas normalmente.
                  {!podeAlterarConfiguracoes && (
                    <>
                      {" "}
                      <strong style={{ color: "var(--ink-soft)" }}>
                        Apenas o responsável pela conta pode alterar esta configuração.
                      </strong>
                    </>
                  )}
                </p>
              </Grid>
            </Grid>

            {podeAlterarConfiguracoes && maxInstrumentadores !== maxInstrumentadoresOriginal && (
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 24, paddingTop: 20, borderTop: "1px solid var(--border)" }}>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setMaxInstrumentadores(maxInstrumentadoresOriginal)}
                  disabled={isSavingConfig}
                >
                  Cancelar
                </Button>
                <Button variant="primary" size="sm" onClick={handleSalvarConfiguracoes} disabled={isSavingConfig}>
                  {isSavingConfig ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : "Salvar alterações"}
                </Button>
              </div>
            )}
          </div>

          <div className="ds-card" style={{ padding: 24 }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
              <div className="ds-icon-box"><FactCheckOutlinedIcon fontSize="small" /></div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h3 style={{ fontSize: 14, fontWeight: 800, margin: 0 }}>Log de atividades</h3>
                <p style={{ fontSize: 12.6, color: "var(--ink-faint)", marginTop: 6, marginBottom: 0 }}>
                  Registra quem fez o quê e quando: exclusões, cancelamentos, mudanças de usuário e
                  de permissão, e tentativas de login sem sucesso. Consultas não são registradas.
                </p>

                <FormControlLabel
                  sx={{ ...switchLabelSx, mt: 1.5 }}
                  control={
                    <Switch
                      size="small"
                      checked={logAtividadeAtivo}
                      disabled={!podeAlterarConfiguracoes || isLoadingEmpresa}
                      onChange={(e) => setLogAtividadeAtivo(e.target.checked)}
                    />
                  }
                  label={
                    <span style={{ fontSize: 13 }}>
                      {logAtividadeAtivo ? "Registrando atividades" : "Não registrar atividades"}
                    </span>
                  }
                />

                {/* Quem liga precisa saber o que está ligando — e quem desliga
                    precisa saber que não está apagando nada. */}
                <p style={{ fontSize: 11.8, color: "var(--ink-faint)", marginTop: 4, marginBottom: 0 }}>
                  O registro ocupa espaço no banco de dados. Desligar não apaga o que já foi
                  gravado — o histórico continua disponível em Relatórios.
                </p>

                {!podeAlterarConfiguracoes && (
                  <p style={{ fontSize: 11.8, color: "var(--ink-faint)", marginTop: 8, marginBottom: 0 }}>
                    Apenas o responsável pela conta pode alterar esta configuração.
                  </p>
                )}
              </div>
            </div>

            {podeAlterarConfiguracoes && logAtividadeAtivo !== logAtividadeOriginal && (
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 20, paddingTop: 16, borderTop: "1px solid var(--border)" }}>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setLogAtividadeAtivo(logAtividadeOriginal)}
                  disabled={isSavingConfig}
                >
                  Cancelar
                </Button>
                <Button variant="primary" size="sm" onClick={handleSalvarConfiguracoes} disabled={isSavingConfig}>
                  {isSavingConfig ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : "Salvar alterações"}
                </Button>
              </div>
            )}
          </div>

          <div className="ds-card" style={{ padding: 24 }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
              <div className="ds-icon-box"><VpnKeyOutlinedIcon fontSize="small" /></div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <h3 style={{ fontSize: 14, fontWeight: 800, margin: 0 }}>Sincronização com sistemas externos</h3>
                  {!temIntegracaoApi && <Badge color="neutral">Não incluso no plano</Badge>}
                </div>
                <p style={{ fontSize: 12.6, color: "var(--ink-faint)", marginTop: 6, maxWidth: 560 }}>
                  Gera uma credencial de longa duração pra sincronização automática de cadastros e
                  cirurgias com sistemas externos, sem depender de login de usuário.
                </p>

                {temIntegracaoApi && (
                  <div style={{ marginTop: 16 }}>
                    {apiKeyGeradaEm && (
                      <p style={{ fontSize: 12.2, color: "var(--ink-faint)", marginBottom: 12 }}>
                        Última chave gerada em{" "}
                        {new Date(apiKeyGeradaEm).toLocaleString("pt-BR")}.
                      </p>
                    )}
                    <Button variant="secondary" size="sm" onClick={handleGerarApiKey} disabled={isGerandoApiKey}>
                      {isGerandoApiKey ? (
                        <CircularProgress size={14} />
                      ) : apiKeyGeradaEm ? (
                        "Gerar nova API Key"
                      ) : (
                        "Gerar API Key de Integração"
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
          </div>
        )}

        <Modal
          open={confirmRegenerarAberto}
          onClose={() => setConfirmRegenerarAberto(false)}
          title="Gerar nova API Key?"
          icon={<WarningAmberOutlinedIcon fontSize="small" />}
          tone="danger"
          actions={
            <>
              <Button variant="ghost" size="sm" onClick={() => setConfirmRegenerarAberto(false)}>
                Cancelar
              </Button>
              <Button variant="danger" size="sm" onClick={executarGeracaoApiKey}>
                Gerar mesmo assim
              </Button>
            </>
          }
        >
          <p style={{ fontSize: 13.2, color: "var(--ink-soft)" }}>
            Já existe uma API key ativa. Gerar uma nova <strong>invalida a anterior imediatamente</strong> —
            qualquer integração do Delphi ainda usando a chave antiga vai parar de funcionar até ser
            atualizada com a nova.
          </p>
        </Modal>

        <Modal
          open={!!apiKeyGerada}
          onClose={() => setApiKeyGerada(null)}
          title="API Key gerada"
          icon={<VpnKeyOutlinedIcon fontSize="small" />}
          wide
          actions={
            <Button variant="primary" size="sm" onClick={() => setApiKeyGerada(null)}>
              Fechar
            </Button>
          }
        >
          <p style={{ fontSize: 13.2, color: "var(--ink-soft)", marginBottom: 14 }}>
            Copie e guarde essa chave agora — por segurança, ela só aparece <strong>uma única vez</strong>.
            Cole no campo de configuração da integração do Delphi.
          </p>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "10px 12px",
              borderRadius: 8,
              border: "1px solid var(--border-strong)",
              background: "var(--surface-2, rgba(127,127,127,0.08))",
            }}
          >
            <code style={{ flex: 1, fontSize: 13, wordBreak: "break-all" }}>{apiKeyGerada}</code>
            <Button variant="ghost" size="sm" onClick={handleCopiarApiKey} icon={<ContentCopyIcon fontSize="inherit" />}>
              Copiar
            </Button>
          </div>
        </Modal>

        {/*
          && isAdm nao e redundante com a aba escondida: montar GestaoUsuarios
          ja dispara GET /usuario/listar. Esconder a aba sem esta guarda
          deixaria a chamada acontecer para quem forcasse ?aba=usuarios.
        */}
        {aba === "usuarios" && isAdm && <GestaoUsuarios />}

        {aba === "seguranca" && (
          <div className="ds-grid-2">
            <div className="ds-card" style={{ padding: 24 }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
                <div
                  className="ds-icon-box"
                  style={{
                    background: darkMode ? "var(--blue-tint)" : "var(--orange-tint)",
                    color: darkMode ? "var(--blue)" : "var(--orange)",
                  }}
                >
                  {darkMode ? <DarkModeOutlinedIcon fontSize="small" /> : <LightModeOutlinedIcon fontSize="small" />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h3 style={{ fontSize: 14, fontWeight: 800, margin: 0 }}>Tema da interface</h3>
                  <p style={{ fontSize: 12.6, color: "var(--ink-faint)", marginTop: 6 }}>
                    Alternar aparência do sistema entre claro e escuro.
                  </p>
                  <div className="ds-theme-toggle" role="group" aria-label="Selecionar tema" style={{ marginTop: 16 }}>
                    <button type="button" className={!darkMode ? "active" : ""} onClick={() => setTheme(false)}>
                      <LightModeOutlinedIcon fontSize="inherit" /> Claro
                    </button>
                    <button type="button" className={darkMode ? "active" : ""} onClick={() => setTheme(true)}>
                      <DarkModeOutlinedIcon fontSize="inherit" /> Escuro
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="ds-card" style={{ padding: 24 }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
                <div className="ds-icon-box"><SecurityOutlinedIcon fontSize="small" /></div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h3 style={{ fontSize: 14, fontWeight: 800, margin: 0 }}>Alterar senha</h3>
                  <p style={{ fontSize: 12.6, color: "var(--ink-faint)", marginTop: 6, marginBottom: 18 }}>
                    Informe a senha atual para confirmar que é você.
                  </p>

                  <Grid container spacing={2.5}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Senha atual"
                        type="password"
                        value={senhaAtual}
                        onChange={(e) => setSenhaAtual(e.target.value)}
                        size="small"
                        fullWidth
                        sx={fieldSx}
                        autoComplete="current-password"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Nova senha"
                        type="password"
                        value={novaSenha}
                        onChange={(e) => setNovaSenha(e.target.value)}
                        size="small"
                        fullWidth
                        sx={fieldSx}
                        autoComplete="new-password"
                        helperText="Mínimo de 6 caracteres"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Confirmar nova senha"
                        type="password"
                        value={confirmarSenha}
                        onChange={(e) => setConfirmarSenha(e.target.value)}
                        size="small"
                        fullWidth
                        sx={fieldSx}
                        autoComplete="new-password"
                      />
                    </Grid>
                  </Grid>

                  {erroSenha && (
                    <div style={{ marginTop: 16 }}>
                      <ErrorBanner message={erroSenha} />
                    </div>
                  )}

                  <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 24, paddingTop: 20, borderTop: "1px solid var(--border)" }}>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setSenhaAtual(""); setNovaSenha(""); setConfirmarSenha(""); setErroSenha(null); }}
                      disabled={isSavingSenha}
                    >
                      Limpar
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleAlterarSenha}
                      disabled={isSavingSenha || !senhaAtual || !novaSenha || !confirmarSenha}
                    >
                      {isSavingSenha ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : "Alterar senha"}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConfiguracoesPage;
